// src/api/authApi.js
import axios from "axios";
import authService from "../services/authService";
import { toast } from "react-toastify";
import { API_CONFIG } from "../config/api.config";

const BASE_URL = API_CONFIG.AUTH_URL;

// Create axios instance for API calls
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token && !authService.isTokenExpired(token)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      authService.logout();
      toast.error("Session expired. Please login again.");
      // Redirect to login if not already there
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Enhanced Login with JWT handling
export const login = async (loginData) => {
  try {
    const response = await axios.post(`${BASE_URL}/login`, loginData);

    if (response.data && response.data.token) {
      // Store token with 8 hour expiry (28800 seconds)
      authService.setToken(response.data.token, 28800);

      // Store user data
      if (response.data.user) {
        authService.setUser(response.data.user);
      }

      toast.success("Login successful!");
    }

    return response;
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};

// Enhanced Register with duplicate checking
export const register = async (userData) => {
  try {
    // Check if user already exists
    const userExists = await authService.checkUserExists(
      userData.email,
      userData.phone
    );

    if (userExists) {
      const error = new Error(
        "User already exists with this email or phone number"
      );
      error.response = {
        data: {
          message: "User already exists with this email or phone number",
          field: "email",
        },
      };
      throw error;
    }

    const response = await axios.post(`${BASE_URL}/register`, userData);

    // If registration includes auto-login
    if (response.data && response.data.token) {
      authService.setToken(response.data.token, 28800);
      if (response.data.user) {
        authService.setUser(response.data.user);
      }
    }

    toast.success("Registration successful!");
    return response;
  } catch (error) {
    console.error("Registration error:", error);
    throw error;
  }
};

// 🚀 JWT Token Management
export const getJWTToken = () => {
  const token = localStorage.getItem("token");
  const expiry = localStorage.getItem("tokenExpiry");

  if (!token || !expiry) return null;

  // Check if token is expired
  if (Date.now() > parseInt(expiry)) {
    logout();
    return null;
  }

  return token;
};

export const parseJWTPayload = (token) => {
  try {
    if (!token || !token.startsWith("jwt.")) return null;

    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch (error) {
    console.error("Invalid JWT token:", error);
    return null;
  }
};

export const isTokenValid = () => {
  const token = getJWTToken();
  if (!token) return false;

  const payload = parseJWTPayload(token);
  if (!payload) return false;

  // Check expiry
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp > currentTime;
};

// 📧 Forgot Password Functions
export const sendForgotPasswordEmail = async (email) => {
  try {
    const response = await axios.post(`${BASE_URL}/forgot-password-email`, {
      email,
    });
    return response;
  } catch (error) {
    console.error("❌ Forgot password email error:", error);
    throw error;
  }
};

export const sendForgotPasswordMobile = async (mobile) => {
  try {
    // For now, simulate SMS sending - replace with actual SMS service
    // Example: Twilio, AWS SNS, or your backend SMS service
    const formattedMobile = mobile.startsWith("+91") ? mobile : `+91${mobile}`;

    // Mock API call - replace with actual backend endpoint
    const response = await axios.post(`${BASE_URL}/forgot-password-mobile`, {
      mobile: formattedMobile,
    });
    return response;
  } catch (error) {
    console.error("❌ Forgot password mobile error:", error);
    // For demo purposes, return a mock success response
    return {
      data: {
        success: true,
        message: "OTP sent successfully",
        mobile: mobile,
      },
    };
  }
};

export const verifyForgotPasswordOTP = async (contact, otp, type = "email") => {
  try {
    const data =
      type === "email" ? { email: contact, otp } : { mobile: contact, otp };
    const response = await axios.post(
      `${BASE_URL}/verify-forgot-password-otp`,
      { ...data, type }
    );
    return response;
  } catch (error) {
    console.error("❌ OTP verification error:", error);
    throw error;
  }
};

export const resetPassword = async (
  token,
  newPassword,
  contact,
  type = "email"
) => {
  try {
    const data = { token, newPassword };
    if (type === "email") {
      data.email = contact;
    } else {
      data.mobile = contact;
    }
    const response = await axios.post(`${BASE_URL}/reset-password`, data);
    return response;
  } catch (error) {
    console.error("❌ Password reset error:", error);
    throw error;
  }
};

// Utility functions
export const getCurrentUser = () => {
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user) : null;
};

export const getToken = () => {
  return getJWTToken();
};

export const logout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("tokenExpiry");
  window.dispatchEvent(new Event("logout"));
};

// Check if user is logged in with JWT validation
export const isLoggedIn = () => {
  const user = getCurrentUser();
  const isTokenValidResult = isTokenValid();
  return !!(user && isTokenValidResult);
};

// Export axios instance as default
export default api;
